import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PageWrapper from "../pageWrapper";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";

//CSS
import "./itemCreationPage.css";
import "../0PageStyles.css";

//Components
import InputField from "../../Components/Other Components/inputField";
import InputGroup from "../../Components/Other Components/inputGroup";
import Button from "../../Components/Other Components/button";
import TagPicker from "../../Components/Tag Components/tagPicker";
import PopupConfirmation from "../../Components/Other Components/popupConfirmation";
import ClientPicker from "../../Components/Client Components/clientPicker";
import PageLoader from "../../Components/Other Components/pageLoader";
import Title from "../../Components/Other Components/Title";
import MandatoryExplanation from "../../Components/Other Components/mandatoryExplanation";
import BarcodeScannerComponent from "../../Components/Other Components/BarcodeScannerComponent";
import ItemInWarehousesView from "../../Components/Item Components/itemInWarehousesView";

//Functions and hooks
import {
  GetItemData,
  DeleteItem,
  EditItem,
} from "../../BackendAccess/ItemHooks";
import { GetUnits } from "../../BackendAccess/UnitHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import useFormState from "../../hooks/useFormStates";
import { getCurrencySymbol, useSettings } from "../../Contexts/SettingsContext";
import { useTranslation } from "react-i18next";

export default function ItemEditPage() {
  const { formData, setField, loadInitialData, hasChanged } = useFormState({
    ItemID: "",
    Barcode: "",
    Name: "",
    Unit: "",
    Price: 0,
    SellingPrice: 0,
    MinStock: "",
    Tags: [],
    Clients: [],
  });
  const [warehousesAmounts, setWarehousesAmounts] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState(false);

  const { showNotification } = useNotification();

  useErrorHandler(error, showNotification, setPageError);

  const [Units, setUnits] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const [showPopup, setShowPopup] = useState(false);

  const { id: currentID } = location.state;

  const [editing, setEditing] = useState(false);
  const [deleteBtnDisabled, setDeleteBtnDisabled] = useState(false);

  const isNarrow = useNarrowMode();

  const { companySettings, refreshCompanySettings } = useSettings();
  const { t } = useTranslation();

  ////////Load data ///////////////////////////////////////
  const GetAndLoadData = async () => {
    setLoading(true);
    try {
      await refreshCompanySettings();
      const [data, error] = await fetchWithTimeout(() =>
        GetItemData(currentID)
      );

      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        LoadData(data);
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentID) {
      GetAndLoadData();
    }
  }, [currentID]);

  async function LoadData(item) {
    const TagList =
      item.Tags !== "/" && item.Tags !== "" ? item.Tags.split(",") : [];
    const ClientList =
      item.Clients !== "/" && item.Clients !== ""
        ? item.Clients.split(",")
        : [];
    const initialValues = {
      ItemID: item.ItemID,
      Barcode: item.Barcode,
      Name: item.Name,
      Unit: item.UnitID,
      Price: item.Price,
      SellingPrice: item.SellingPrice,
      MinStock: item.MinStock ?? "",
      Tags: TagList,
      Clients: ClientList,
      LastEditedBy:
        item.FirstName + " " + item.LastName + " (" + item.LastEditedBy + ")",
    };
    console.log("Init item unit", item);

    loadInitialData(initialValues);

    const [UnitList, UnitError] = await GetUnits();
    if (UnitError) {
      UnitError.isPageError = true;
      setError(UnitError);
      return;
    } else {
      setUnits(
        UnitList.map((item) => {
          return { value: item.UnitID, label: item.Name };
        })
      );
    }

    setWarehousesAmounts(
      item.WarehouseNames.map((warehouseName, index) => {
        return {
          Warehouse: warehouseName,
          Amount: item.Amounts[index],
          WarehouseID: item.WarehouseIDs[index],
        };
      })
    );
    setTotalAmount(item.TotalAmount);
  }

  ////////Load data END///////////////////////////////////////

  ////////Handle input changes///////////////////////////////////////
  const handleInputChange = (field, value) => {
    if (field === "Price") {
      if (/^[0-9]*\.?[0-9]{0,2}$/.test(value)) {
        setField(field, value);
      } else {
        setError({
          err: t(
            "validation.Price can only be a number, and have 2 decimal digits.",
            { ns: "error" }
          ),
          errCode: 400,
        });
      }
    } else if (field === "SellingPrice") {
      if (/^(?:$|(?:\d+(?:\.\d{1,2})?|\.\d{1,2}))$/.test(value)) {
        setField(field, value);
      } else {
        setError({
          err: t(
            "validation.Price can only be a number, and have 2 decimal digits.",
            { ns: "error" }
          ),
          errCode: 400,
        });
      }
    } else if (field === "MinStock") {
      if (value === "" || /^[0-9]*$/.test(value)) {
        setField(field, value);
      }
    } else setField(field, value);
  };

  ////////Handle input changes END///////////////////////////////////////

  const OnConfirmChanges = async () => {
    showNotification(t("Editing item...", { ns: "items" }), "info");
    setEditing(true);
    try {
      const [editData, editError] = await EditItem(
        currentID,
        formData.ItemID,
        formData.Barcode,
        formData.Name,
        formData.Unit,
        formData.Price,
        formData.SellingPrice,
        formData.Tags.toString(),
        formData.Clients.toString(),
        formData.MinStock
      );
      if (editError) {
        setError(editError);
        return;
      }
      showNotification(
        t("Item edited successfully!", { ns: "items" }),
        "success"
      );
      navigate(-1);
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setEditing(false);
    }
  };

  const clickOnDelete = () => {
    setShowPopup(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteBtnDisabled(true);
    showNotification(t("Deleting item...", { ns: "items" }), "info");
    try {
      const [deleteData, deleteError] = await DeleteItem(currentID);
      if (deleteError) {
        setError(deleteError);
        return;
      }
      showNotification(
        t("Item deleted successfully!", { ns: "items" }),
        "success"
      );
      navigate(-1);
    } catch (err) {
      setError({ err: "Server error.", errCode: 500 });
    } finally {
      setDeleteBtnDisabled(false);
    }
  };

  const handleCancelDelete = () => {
    setShowPopup(false);
  };

  const clickOnCancel = () => {
    showNotification(t("Item editing canceled.", { ns: "items" }), "info");
    navigate(-1);
  };

  const tutorialSteps = [
    {
      target: null,
      title: t("Editing an item", { ns: "tutorial" }),
      message: t(
        "This page shows one item's full details, everywhere it's stocked, and lets you change or delete it. We'll walk through every field.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-edit-id",
      title: t("Item ID", { ns: "tutorial" }),
      message: t(
        "The item's unique ID. This is fixed and can't be changed after the item was created.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-edit-barcode",
      title: t("Barcode", { ns: "tutorial" }),
      message: t(
        "Optional. A barcode number, a link, or any custom text used to identify this item at checkout or on a shipping label.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-edit-scan-barcode",
      title: t("Scan instead of typing", { ns: "tutorial" }),
      message: t(
        "Rather than typing the barcode by hand, you can tap here to scan a new one with your device's camera.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-edit-name",
      title: t("Name", { ns: "tutorial" }),
      message: t(
        "The item's name, as it's shown everywhere in the app - item lists, traffic notes, reports.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-edit-unit",
      title: t("Unit", { ns: "tutorial" }),
      message: t(
        "How this item is measured and counted - for example pieces, kilograms, or liters.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-edit-price",
      title: t("Price", { ns: "tutorial" }),
      message: t(
        "The price you pay to acquire this item - its purchase price. Shown in your company's configured currency.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-edit-selling-price",
      title: t("Selling price", { ns: "tutorial" }),
      message: t(
        "The price you sell this item for. This is used to calculate profit margins in the inventory report.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-edit-min-stock",
      title: t("Minimum stock", { ns: "tutorial" }),
      message: t(
        "Optional. Set a quantity threshold - once this item's total stock across all warehouses drops below it, it'll show up on the Low Stock page as a warning.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-edit-tags",
      title: t("Tags", { ns: "tutorial" }),
      message: t(
        "Tags help you group and filter items later - for example by category, storage requirements, or fragility.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-edit-suppliers",
      title: t("Suppliers", { ns: "tutorial" }),
      message: t(
        "Optionally link this item to the supplier or manufacturer you source it from, so you can track where your stock comes from.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-edit-warehouses",
      title: t("Stock by warehouse", { ns: "tutorial" }),
      message: t(
        "See exactly how much of this item is stocked in each warehouse, and the total across all of them.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-edit-apply-btn",
      title: t("Save your changes", { ns: "tutorial" }),
      message: t(
        "Once you're happy with your changes, click here to save them. This button only lights up once something has actually changed.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "item-edit-delete-btn",
      title: t("Delete", { ns: "tutorial" }),
      message: t(
        "Permanently deletes this item and removes it from all traffic records. This cannot be undone, so use it carefully.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="itemEdit" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <Title className="mt-3">{formData.Name}</Title>
          <div className="container-bordered item-edit w-90 vertical mb-5 mt-4 ">
          <InputGroup className="w-100">
            <InputField
              className="mb-2"
              placeholder={t("Example: 1001")}
              label={t("ID") + ":"}
              value={formData.ItemID}
              onChange={(e) => {
                handleInputChange("ItemID", e);
              }}
              mandatory={true}
              readOnly={true}
              tutorialId="item-edit-id"
            />
            <InputField
              placeholder={t("Example: 1234542,some-link.com,custom text", {
                ns: "items",
              })}
              label={t("Barcode") + ":"}
              value={formData.Barcode}
              onChange={(e) => {
                handleInputChange("Barcode", e);
              }}
              className="mb-2"
              tutorialId="item-edit-barcode"
            />
            <div data-tutorial-id="item-edit-scan-barcode">
              <BarcodeScannerComponent
                code={formData.Barcode}
                onScanSuccess={(e) => {
                  handleInputChange("Barcode", e);
                }}
              />
            </div>
            <InputField
              className="mb-2"
              placeholder={t("Example: Small bolt", { ns: "items" })}
              label={t("Name") + ":"}
              value={formData.Name}
              onChange={(e) => {
                handleInputChange("Name", e);
              }}
              mandatory={true}
              tutorialId="item-edit-name"
            />
            <InputField
              className="mb-2"
              placeholder={t("Example: kg", { ns: "items" })}
              label={t("Unit") + ":"}
              value={formData.Unit}
              onChange={(e) => {
                handleInputChange("Unit", e);
              }}
              type="dropdown"
              options={Units}
              mandatory={true}
              tutorialId="item-edit-unit"
            />
            <InputField
              placeholder={t("Example: 120.20", { ns: "items" })}
              label={
                t("Price") +
                ": (" +
                getCurrencySymbol(companySettings.currency) +
                ")"
              }
              value={formData.Price}
              onChange={(e) => {
                handleInputChange("Price", e);
              }}
              className="mb-2"
              mandatory={true}
              tutorialId="item-edit-price"
            />
            <InputField
              placeholder={t("Example: 120.20", { ns: "items" })}
              label={
                t("Selling Price") +
                ": (" +
                getCurrencySymbol(companySettings.currency) +
                ")"
              }
              value={formData.SellingPrice}
              onChange={(e) => {
                handleInputChange("SellingPrice", e);
              }}
              className="mb-2"
              mandatory={true}
              tutorialId="item-edit-selling-price"
            />
            <InputField
              placeholder={t("Example: 10", { ns: "items" })}
              label={t("Minimum Stock", { ns: "items" }) + ":"}
              value={formData.MinStock}
              onChange={(e) => {
                handleInputChange("MinStock", e);
              }}
              className="mb-2"
              description={t(
                "Get a low-stock warning once total quantity across all warehouses falls below this",
                { ns: "items" }
              )}
              tutorialId="item-edit-min-stock"
            />
            <MandatoryExplanation />
          </InputGroup>
          </div>

          <div className="container-bordered item-edit horizontal mb-2 w-90">
            <div
              data-tutorial-id="item-edit-tags"
              className={isNarrow ? "mb-4 w-100" : "mr-4 w-100"}
            >
              <TagPicker
                onChange={(e) => {
                  handleInputChange("Tags", e);
                }}
                initSelectedTags={formData.Tags}
              ></TagPicker>
            </div>
            <div
              data-tutorial-id="item-edit-suppliers"
              className={isNarrow ? "w-100" : "ml-4 w-100"}
            >
              <ClientPicker
                title="Suppliers"
                onChange={(e) => {
                  handleInputChange("Clients", e);
                }}
                typesToShow={["Manufacturer", "Supplier"]}
                PickedClients={formData.Clients}
              ></ClientPicker>
            </div>
          </div>
          <div data-tutorial-id="item-edit-warehouses">
            <ItemInWarehousesView
              WarehouseAmounts={warehousesAmounts}
              totalItems={totalAmount}
            />
          </div>
          <span className="last-edited-by-bar w-90">
            <span className="sec-textcolor-span">{t("Last edited by:")} </span>
            &nbsp;
            {formData.LastEditedBy}
          </span>

          <div className="btn-container">
            <Button
              btnMargin={"btn-default"}
              btnStyle={
                hasChanged && !editing ? "btn-positive" : "btn-disabled"
              }
              onClick={OnConfirmChanges}
              acceptEnter={true}
              icon={"apply"}
              tutorialId="item-edit-apply-btn"
            >
              {t("Apply Changes")}
            </Button>
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-primary"
              onClick={clickOnCancel}
            >
              {t("Cancel")}
            </Button>
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-negative"
              onClick={clickOnDelete}
              icon={"delete"}
              disabled={deleteBtnDisabled}
              tutorialId="item-edit-delete-btn"
            >
              {t("Delete")}
            </Button>
          </div>
        </div>
        {showPopup && (
          <PopupConfirmation
            style={"warning"}
            message={
              <p>
                {t("Are you sure you want to delete this item?", {
                  ns: "items",
                })}
                <br />{" "}
                {t(
                  "This action will remove this item from all traffic records!",
                  { ns: "items" }
                )}
                <br />
                <span className="text-negative">
                  {t("This action cannot be undone.")}
                </span>
              </p>
            }
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
          />
        )}
      </PageLoader>
    </PageWrapper>
  );
}
